<template>
	<!-- Filter by Goal Type -->
	<div class="q-pt-sm q-mt-sm app-data-table-toolbar app-data-table-toolbar--single">
		<q-select
			v-model="selectedGoalTypes"
			:options="goalTypeOptions"
			label="Filter by Goal Types"
			multiple
			dense
			clearable
			@clear="selectedGoalTypes = []"
			use-chips
			outlined
			class="full-width"
		/>
	</div>
	<!-- Client Goals Table -->
	<div class="goal-table">
		<q-table
			class="app-data-table"
			:rows="selectedGoalTypes.length > 0 ? goals.filter(g => selectedGoalTypes.map(opt => opt.value).includes(g.wellbeing_id)) : goals"
			:columns="columns"
			row-key="id"
			:loading="!loaded"
			:pagination="{ rowsPerPage: 7 }"
			:rows-per-page-options="[0]"
			flat
		>
			<template v-slot:body-cell-description="props">
				<q-td :props="props" @click="openEdit(props.row, props.rowIndex)">
					<div class="truncate-line a-tag" :title="props.row.description">{{ props.row.description }}
						<q-tooltip style="max-width: 600px;" class="text-body2 text-black bg-white">{{ props.row.description }}</q-tooltip>
					</div>
				</q-td>
			</template>
			<template v-slot:body-cell-date_set="props">
				<q-td :props="props">{{ formatDate(props.row.date_set) }}</q-td>
			</template>
			<template v-slot:body-cell-status="props">
				<q-td :props="props">
					<q-chip
						:color="statusColor(props.row.status)"
						:icon="props.row.loading ? undefined : statusIcon(props.row.status)"
						text-color="white" dense size="md"
						style="font-size: 13px; cursor: pointer;"
						clickable
					>
						<q-spinner v-if="props.row.loading" size="xs" color="white" class="q-mr-xs" />
						{{ props.row.status || 'Ongoing' }}
						<q-tooltip class="text-body2 text-black bg-white">Click to change status</q-tooltip>
						<q-menu v-if="!props.row.loading" auto-close>
							<q-list dense style="min-width: 170px;">
								<q-item v-for="s in goalStatuses" :key="s" clickable v-close-popup @click="changeStatus(props.row, s)">
									<q-item-section side><q-icon :name="statusMeta[s]?.icon || 'label'" :color="statusMeta[s]?.color || 'grey-6'" size="xs" /></q-item-section>
									<q-item-section :class="'text-' + (statusMeta[s]?.color || 'grey-6')">{{ s }}</q-item-section>
								</q-item>
							</q-list>
						</q-menu>
					</q-chip>
				</q-td>
			</template>
			<template v-slot:body-cell-date_of_status="props">
				<q-td :props="props">{{ formatDate(props.row.date_of_status) }}</q-td>
			</template>
			<template v-slot:body-cell-actions="props">
				<q-td :props="props">
					<div class="actions-cell">
						<div v-if="props.row.notes" class="action-slot">
							<q-icon size="sm" name="article" color="secondary">
								<q-tooltip class="text-body2 text-black bg-white" max-width="300px" max-height="250px">{{ props.row.notes }}</q-tooltip>
							</q-icon>
						</div>
						<div class="action-slot">
							<q-icon class="icon-hover" size="sm" color="grey-7" name="edit" @click="openEdit(props.row, props.rowIndex)">
								<q-tooltip class="text-body2 text-black bg-white">Edit Goal</q-tooltip>
							</q-icon>
						</div>
					</div>
				</q-td>
			</template>
			<template v-if="loaded" v-slot:no-data>
				<div class="q-pa-md text-grey-7">No goals. Click "New Goal" or complete a Te Iti Kahurangi form.</div>
			</template>
			<template v-slot:loading>
				<div class="q-pa-md text-grey-7">Loading goals...</div>
			</template>
		</q-table>

		<!-- Add/Edit Goal Modal -->
		<q-dialog v-model="editDialog">
			<q-card style="width: 600px;" class="relative-position">
				<form @submit.prevent="editingIndex === null ? saveAdd() : saveEdit()">
					<q-card-section class="q-gutter-md">
						<div class="text-h6">{{ editingIndex === null ? 'Create Goal' : 'Edit Goal' }}</div>
						<q-input
							:rules="[val => validDescription(val) || 'Description is required.']"
							type="textarea" required
							v-model="editModel.description"
							label="Title / Description *"
							dense
							outlined
							:input-style="{ resize: 'none', height: '60px' }"
						/>
						<q-select
							v-model="editModel.wellbeing_id" required
							:options="goalTypeOptions"
							label="Goal Type *"
							dense outlined map-options emit-value
						/>
						<q-input :rules="[val => validDateSet(val) || 'Date Set is required.']" v-model="editModel.date_set" required label="Date Set *" stack-label dense outlined type="date" />
					<q-select
						v-model="editModel.status"
						:options="filteredGoalStatuses"
						label="Status"
						dense outlined
						use-input
						input-debounce="0"
						new-value-mode="add-unique"
						@filter="filterGoalStatuses"
					>
						<template v-slot:selected-item="scope">
							<div class="row items-center q-gutter-xs">
								<q-icon :name="statusMeta[scope.opt]?.icon || 'label'" :color="statusMeta[scope.opt]?.color || 'grey-6'" size="xs" />
								<span :class="'text-' + (statusMeta[scope.opt]?.color || 'grey-6')">{{ scope.opt }}</span>
							</div>
						</template>
						<template v-slot:option="scope">
							<q-item v-bind="scope.itemProps">
								<q-item-section side>
									<q-icon :name="statusMeta[scope.opt]?.icon || 'label'" :color="statusMeta[scope.opt]?.color || 'grey-6'" size="xs" />
								</q-item-section>
								<q-item-section :class="'text-' + (statusMeta[scope.opt]?.color || 'grey-6')">{{ scope.opt }}</q-item-section>
							</q-item>
						</template>
						<template v-slot:after-options>
							<q-item dense class="text-grey-5 q-px-md">
								<q-item-section side><q-icon name="edit" size="xs" color="grey-5" /></q-item-section>
								<q-item-section class="text-caption">Type a custom status &amp; press Enter</q-item-section>
							</q-item>
						</template>
					</q-select>
					<q-input v-model="editModel.date_of_status" label="Date of Status" stack-label dense outlined type="date" clearable />
						<q-input type="textarea" v-model="editModel.notes" label="Additional Notes" dense outlined :input-style="{ resize: 'none' }" />
					</q-card-section>
					<q-card-actions class="row justify-between q-mr-sm q-mb-sm">
						<q-btn v-if="editingIndex !== null" no-caps flat label="Delete" color="red" @click="confirmDeleteFromEdit" :disable="editLoading" />
						<div v-else />
						<div class="row q-gutter-sm">
							<q-btn no-caps flat label="Cancel" color="grey" @click="cancelEdit" :disable="editLoading" />
							<q-btn no-caps unelevated label="Save" color="primary" type="submit" :loading="editLoading" :disable="editLoading" />
						</div>
					</q-card-actions>
					<q-inner-loading :showing="editLoading" color="primary">
						<q-spinner size="40px" color="primary" />
					</q-inner-loading>
				</form>
			</q-card>
		</q-dialog>
		<q-dialog v-model="deleteDialog">
			<q-card style="width: 400px;">
				<q-card-section>
					<div class="text-h6">Confirm Delete</div>
					<p>Are you sure you want to delete this goal?</p>
					<p class="text-body-2 wrap-lines-3" align="center">{{ deleteModel.description }}</p>
				</q-card-section>
				<q-card-actions align="right">
					<q-btn flat label="Cancel" color="grey" @click="deleteDialog = false" :disable="deleteLoading" no-caps />
					<q-btn flat label="Delete" color="red" @click="deleteGoal()" :loading="deleteLoading" :disable="deleteLoading" no-caps />
				</q-card-actions>
			</q-card>
		</q-dialog>
	</div>
	<!-- Add button (below table for z-index) -->
	<q-btn fab-mini label="New Goal" no-caps color="primary" flat class="absolute-top-right q-mr-sm q-mt-sm" @click="openAdd"/>
	<!-- Delete modal -->
</template>

<script>
import goalAPI from "@/services/api/goals";
import notify from "@/services/util/notify";

export default {
	name: "GoalTable",
	props: {
		client_id: {
			type: [String, Number],
			default: null
		},
		personId: {
			type: [String, Number],
			default: null
		},
		engagementId: {
			type: [String, Number],
			default: null
		}
	},
	computed: {
		isEngagementGoals() {
			return this.personId !== null && this.engagementId !== null;
		},
		ownerKey() {
			return this.isEngagementGoals
				? `engagement:${this.personId}:${this.engagementId}`
				: `client:${this.client_id}`;
		}
	},
	data() {
		return {
			goals: [],
			selectedGoalTypes: [],
			loaded: false,
			error: null,
			columns: [
				{ name: 'description', label: 'Description', field: 'description', align: 'left', sortable: true, style: 'max-width: 200px' },
				{ name: 'wellbeing_area', label: 'Goal Type', field: 'wellbeing_area', align: 'left', sortable: true },
				{ name: 'date_set', label: 'Date Set', field: 'date_set', align: 'left', sortable: true, sort: (a, b) => {
					if (!a && !b) return 0;
					if (!a) return 1;
					if (!b) return -1;
					return new Date(a).getTime() - new Date(b).getTime();
				}},
				{ name: 'status', label: 'Status', field: 'status', align: 'left', sortable: true },
				{ name: 'date_of_status', label: '', field: 'date_of_status', align: 'left', sortable: true, sort: (a, b) => {
					if (!a && !b) return 0;
					if (!a) return 1;
					if (!b) return -1;
					return new Date(a).getTime() - new Date(b).getTime();
				}},
                { name: 'actions', field: 'actions', align: 'right'}
			],
			editDialog: false,
			editModel: { id: null, description: '', wellbeing_id: 0, date_set: '', status: 'Ongoing', date_of_status: '', notes: '' },
			editingIndex: null,
			editLoading: false,
			deleteDialog: false,
			deleteModel: { id: null, description: '' },
			deleteLoading: false,
			goalStatuses: ['Ongoing', 'Updated', 'Achieved', 'Unsuccessful', 'Not Achievable', 'No longer applicable', 'Abandoned', 'Discontinued'],
			filteredGoalStatuses: ['Ongoing', 'Updated', 'Achieved', 'Unsuccessful', 'Not Achievable', 'No longer applicable', 'Abandoned', 'Discontinued'],
			statusMeta: {
				'Ongoing':        { icon: 'autorenew',    color: 'primary' },
				'Updated':        { icon: 'update',       color: 'info' },
				'Achieved':       { icon: 'check_circle', color: 'positive' },
				'Unsuccessful':   { icon: 'cancel',       color: 'black' },
				'Not Achievable': { icon: 'block',        color: 'orange-8' },
				'No longer applicable': { icon: 'not_interested', color: 'grey-7' },
				'Abandoned':      { icon: 'exit_to_app',  color: 'grey-8' },
				'Discontinued':   { icon: 'stop_circle',  color: 'grey-8' },
			},
			goalTypeOptions: [ // fetched from db, but hardcoded here for safe fallback
				{ label: 'Ukaipo (Relationships)', colLabel: 'Ukaipo', value: 1 },
				{ label: 'Hinengaro (Mind)', colLabel: 'Hinengaro', value: 2 },
				{ label: 'Tinana (Physical)', colLabel: 'Tinana', value: 3 },
				{ label: 'Taiao (Environment)', colLabel: 'Taiao', value: 4 },
				{ label: 'Rawa (Personal Resources)', colLabel: 'Rawa', value: 5 },
				{ label: 'Wairua (Spiritual)', colLabel: 'Wairua', value: 6 }
			]
		};
	},
	methods: {
		listGoalRequest(params = {}) {
			if (this.isEngagementGoals) {
				return goalAPI.listEngagementGoals(this.personId, this.engagementId, params);
			}
			return goalAPI.listGoals({ ...params, client_id: this.client_id });
		},
		createGoalRequest(payload) {
			if (this.isEngagementGoals) {
				return goalAPI.createEngagementGoal(this.personId, this.engagementId, payload);
			}
			return goalAPI.createGoal({ ...payload, client_id: this.client_id });
		},
		updateGoalRequest(goalId, payload) {
			if (this.isEngagementGoals) {
				return goalAPI.updateEngagementGoal(this.personId, this.engagementId, goalId, payload);
			}
			return goalAPI.updateGoal(goalId, { ...payload, client_id: this.client_id });
		},
		deleteGoalRequest(goalId) {
			if (this.isEngagementGoals) {
				return goalAPI.deleteEngagementGoal(this.personId, this.engagementId, goalId);
			}
			return goalAPI.deleteGoal(goalId, this.client_id);
		},
		invalidateGoalCache() {
			if (this.isEngagementGoals) {
				goalAPI.invalidateEngagementCache(this.personId, this.engagementId);
				return;
			}
			goalAPI.invalidateCache(this.client_id);
		},
		resetForm() {
			const t = new Date();
			const todayStr = `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
			this.editModel = { id: null, description: '', wellbeing_id: this.goalTypeOptions.length > 0 ? this.goalTypeOptions[0].value : 0, date_set: todayStr, status: 'Ongoing', date_of_status: '', notes: '' };
		},
		async fetchWellbeing() {
			try {
				const res = await goalAPI.listGoalTypes();
				if (Array.isArray(res.data) && res.data.length > 0) {
					this.goalTypeOptions = res.data.map(g => ({
						label: g.description,
						colLabel: g.title,
						value: g.id
					}));
				}
			} catch (e) {
				console.error("Failed to load goal types", e);
				// will use hardcoded fallback
			}
		},
		async fetchGoals() {
			try {
				const res = await this.listGoalRequest();
				// Expect array of goals objects
				this.goals = Array.isArray(res.data) ? res.data : [];
				this.goals.forEach(g => {
					if (!g.wellbeing_area && g.wellbeing_id !== null) {
						const opt = this.goalTypeOptions.find(o => o.value === g.wellbeing_id);
						g.wellbeing_area = opt ? opt.colLabel : '(Unknown)';
					}
				});
			} catch (e) {
				this.error = e;
				console.error("Failed to load goals", e);
			}
		},
		async refreshGoals() {
			this.invalidateGoalCache();
			await this.fetchGoals();
		},
		validDescription(desc) {
			return desc && desc.trim() !== '';
		},
		validDateSet(dateSet) {
			return dateSet && dateSet.trim() !== '';
		},
		validateForm() {
			if (!this.validDescription(this.editModel.description)) {
				notify.error("Description is required.", "top");
				return false;
			}
			if (!this.validDateSet(this.editModel.date_set)) {
				notify.error("Date Set is required.", "top");
				return false;
			}
			return true;
		},
		filterGoalStatuses(val, update) {
			if (val === '') {
				update(() => { this.filteredGoalStatuses = [...this.goalStatuses]; });
				return;
			}
			const needle = val.toLowerCase();
			update(() => {
				this.filteredGoalStatuses = this.goalStatuses.filter(s => s.toLowerCase().includes(needle));
			});
		},
		statusColor(status) {
			return this.statusMeta[status]?.color || 'grey-6';
		},
		statusIcon(status) {
			return this.statusMeta[status]?.icon || 'label';
		},
		// For display purposes, DD/MM/YYYY
		formatDate(d) {
			if (!d) return "";
			try { return new Date(d).toLocaleDateString(); } catch(_) { return d; }
		},
		// For value purposes, YYYY-MM-DD
		sanitiseDate(val) {
			if (!val) return null;
			if (typeof val === 'string') {
				const d = new Date(val);
				if (!isNaN(d.getTime())) {
					const m = (d.getMonth()+1).toString().padStart(2,'0');
					const day = d.getDate().toString().padStart(2,'0');
					const y = d.getFullYear();
					return `${y}-${m}-${day}`;
				}
			} else if (val instanceof Date) {
				if (!isNaN(val.getTime())) {
					return val.toISOString();
				}
			}
		},

		openAdd() {
			this.resetForm();
			this.editDialog = true;
		},

		openEdit(goal, index) {
			this.editModel = { ...goal };

			['date_set','date_of_status'].forEach(f => {
				this.editModel[f] = this.sanitiseDate(this.editModel[f]);
			});
			this.editingIndex = index;
			this.editDialog = true;
		},

		cancelEdit() {
			this.editDialog = false;
			this.resetForm();
			this.editingIndex = null;
		},

		confirmDeleteFromEdit() {
			this.deleteModel = { id: this.editModel.id, description: this.editModel.description };
			this.editDialog = false;
			this.editingIndex = null;
			this.deleteDialog = true;
		},

		confirmDelete(goal) {
			this.deleteModel = { id: goal.id, description: goal.description };
			this.deleteDialog = true;
		},

		async deleteGoal() {
			try {
				this.deleteLoading = true;
				await this.deleteGoalRequest(this.deleteModel.id);
				// Remove from local list
				this.goals = this.goals.filter(g => g.id !== this.deleteModel.id);
				this.invalidateGoalCache();
				this.deleteDialog = false;
			} catch (err) {
				notify.error("An error occurred while deleting this goal. Please try again later.", { position: 'top' });
				console.log(err);
			} finally {
				this.deleteLoading = false;
			}
		},
		async saveAdd() {
			if (!this.validateForm()) return;
			try {
				this.editLoading = true;
				const newGoal = await this.createGoalRequest({
					description: this.editModel.description,
					wellbeing_id: this.editModel.wellbeing_id,
					date_set: this.editModel.date_set ? new Date(this.editModel.date_set).toISOString() : null,
					status: this.editModel.status || 'Ongoing',
					date_of_status: this.editModel.date_of_status ? new Date(this.editModel.date_of_status).toISOString() : null,
					notes: this.editModel.notes
				});
				if (newGoal && newGoal.data) {
					newGoal.data.wellbeing_area = this.goalTypeOptions.find(opt => opt.value === newGoal.data.wellbeing_id)?.colLabel || newGoal.data.wellbeing_area;
					this.goals.push(newGoal.data);
				}
				this.invalidateGoalCache();
				this.cancelEdit();
			} catch (err) {
				notify.error("An error occurred while adding this goal. Please try again later.", { position: 'top' });
				console.log(err);
			} finally {
				this.editLoading = false;
			}
		},
	
		async saveEdit() {
			if (!this.validateForm()) return;
			try {
				this.editLoading = true;
				await this.updateGoalRequest(this.editModel.id, {
					description: this.editModel.description,
					wellbeing_id: this.editModel.wellbeing_id,
					date_set: this.editModel.date_set ? new Date(this.editModel.date_set).toISOString() : null,
					status: this.editModel.status || 'Ongoing',
					date_of_status: this.editModel.date_of_status ? new Date(this.editModel.date_of_status).toISOString() : null,
					notes: this.editModel.notes
				});
				// update in place
				const existingGoal = this.goals.find(g => g.id === this.editModel.id);
				if (existingGoal) {
					existingGoal.description = this.editModel.description;
					existingGoal.wellbeing_id = this.editModel.wellbeing_id;
					existingGoal.wellbeing_area = this.goalTypeOptions.find(opt => opt.value === this.editModel.wellbeing_id)?.colLabel || existingGoal.wellbeing_area;
					existingGoal.date_set = this.editModel.date_set ? new Date(this.editModel.date_set).toISOString() : null;
					existingGoal.status = this.editModel.status || 'Ongoing';
					existingGoal.date_of_status = this.editModel.date_of_status ? new Date(this.editModel.date_of_status).toISOString() : null;
					existingGoal.notes = this.editModel.notes;
				}
				this.invalidateGoalCache();
				this.cancelEdit();
			} catch (err) {
				notify.error("An error occurred while editing this goal. Please try again later.", { position: 'top' });
				console.log(err);
			} finally {
				this.editLoading = false;
			}
		},
		
		async changeStatus(row, newStatus) {
			row.loading = true;
			try {
				const now = new Date().toISOString();
				const updates = {
					status: newStatus,
					date_of_status: newStatus !== 'Ongoing' ? now : null
				};
				await this.updateGoalRequest(row.id, updates);
				row.status = newStatus;
				row.date_of_status = updates.date_of_status;
				this.invalidateGoalCache();
			} catch (e) {
				console.error('Failed to change status:', e);
				notify.error('An error occurred while updating the status. Please try again later.', { position: 'top' });
			} finally {
				row.loading = false;
			}
		},

		async markComplete(goalId) {
			const timestamp = new Date().toISOString();
			const goal = this.goals.find(g => g.id === goalId);
			if (goal) goal.loading = true;
			try {
				await this.updateGoalRequest(goalId, {
					status: 'Achieved',
					date_of_status: timestamp
				});
				const currentGoal = this.goals.find(g => g.id === goalId);
				if (currentGoal) {
					currentGoal.status = 'Achieved';
					currentGoal.date_of_status = timestamp;
				}
				this.invalidateGoalCache();
			} catch (e) {
				console.error("Failed to mark complete:", e);
				notify.error("An error occurred while marking this goal complete. Please try again later.", { position: 'top' });
			} finally {
				if (goal) goal.loading = false;
			}
		},

		async markIncomplete(goalId) {
			const goal = this.goals.find(g => g.id === goalId);
			if (goal) goal.loading = true;
			try {
				await this.updateGoalRequest(goalId, {
					status: 'Ongoing'
				});
				const currentGoal = this.goals.find(g => g.id === goalId);
				if (currentGoal) {
					currentGoal.status = 'Ongoing';
					currentGoal.date_of_status = null;
				}
				this.invalidateGoalCache();
			} catch (e) {
				console.error("Failed to mark incomplete:", e);
				notify.error("An error occurred while marking this goal incomplete. Please try again later.", { position: 'top' });
			} finally {
				if (goal) goal.loading = false;
			}
		}
	},
	async created() {
		this.error = null;
        this.loaded = false;
		await this.fetchWellbeing();
		await this.fetchGoals();
		this.loaded = true;
	},
	watch: {
		async ownerKey(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.loaded = false;
				await this.fetchGoals();
				this.loaded = true;
			}
		}
	}
};
</script>

<style scoped>
	.achieve-check {
		color: grey;
		cursor: pointer;
		transition: color .15s;
	}
	.achieve-check:hover {
		color: green;
	}

	.actions-cell {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 12px;
		white-space: nowrap;
	}
	.action-slot {
		width: 28px;
		height: 28px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.icon-hover { cursor: pointer; }
	.truncate-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
	}

	.a-tag {
		color: var(--q-primary);
		cursor: pointer;
		text-decoration: underline;
	}

	.wrap-lines-3 {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		overflow: hidden;
		word-break: break-word;
	}
</style>
